import { IMAGES } from "@/lib/site";

export const POSTS_B = [
    {
        slug: "birthday-night-bangkok-vip",
        title: "How to Plan an Unforgettable Birthday Night in Bangkok",
        meta: "Planning a birthday in Bangkok? How to arrange a VIP table, pick the right nightlife area, and make the celebration feel effortless with a concierge.",
        excerpt: "Rooftop toasts, a table with your name on it, and a midnight moment — how to plan a Bangkok birthday that actually feels special.",
        category: "Occasions",
        readTime: "4 min read",
        image: IMAGES.bangkok,
        sections: [
            {
                h: "Start above the city",
                b: "Bangkok's rooftops are made for birthday toasts — skyline views, early-evening energy, and photos worth keeping. Begin there, then move down to the clubs as the night builds.",
            },
            {
                h: "The table is the gift",
                b: "A reserved VIP table turns a birthday from a night out into an occasion: your space, your bottles, and a natural moment for the celebration to peak. Tell us it's a birthday when you book — the details matter.",
            },
            {
                h: "Timing the night",
                b: "Birthdays run on moments: dinner around eight, rooftop around ten, club by midnight. Booking ahead means each chapter is already arranged and nobody spends the night on logistics.",
            },
            {
                h: "Make it effortless",
                b: "Send the BookMyVIP concierge your date, headcount and the birthday person's style. We'll suggest VIP table options across Sukhumvit, Thonglor or RCA and handle the booking process for you.",
            },
        ],
    },
    {
        slug: "thailand-nightlife-first-timers",
        title: "Thailand Nightlife for First-Timers: What to Know Before You Go Out",
        meta: "First night out in Thailand? Practical tips on dress codes, ID, timing, getting around and booking VIP tables — everything first-timers should know.",
        excerpt: "Dress codes, ID, timing and taxis — the practical stuff nobody tells first-timers about Thailand's nightlife, plus how to skip the guesswork entirely.",
        category: "Guides",
        readTime: "6 min read",
        image: IMAGES.hero,
        sections: [
            {
                h: "Dress the part",
                b: "Premium venues in Bangkok, Phuket and Koh Samui enforce smart-casual dress codes: closed shoes, no singlets, no beachwear after dark. When in doubt, dress one level smarter than you think you need to.",
            },
            {
                h: "Carry your ID",
                b: "Clubs check identification at the door, and the legal drinking age in Thailand is 20. Bring your passport or a clear photo of it — being turned away over ID is a avoidable way to end a night early.",
            },
            {
                h: "Nights start late",
                b: "Thailand's clubs warm up after 11pm and peak well past midnight. Plan dinner and sunset drinks first, and don't arrive at peak venues too early or too late — your concierge can advise on timing for your specific night.",
            },
            {
                h: "Getting around safely",
                b: "Use metered taxis or ride-hailing apps between venues, agree tuk-tuk prices before you get in, and keep your group together when moving between areas at night.",
            },
            {
                h: "Skip the guesswork",
                b: "The easiest first night is a planned one. BookMyVIP's concierge arranges VIP tables and explains the booking process before you arrive — so your first Thailand night out feels like your tenth.",
            },
        ],
    },
    {
        slug: "book-vip-table-before-you-fly",
        title: "Why You Should Book Your VIP Table Before You Fly to Thailand",
        meta: "Advance VIP table booking in Thailand means better tables, better positions and zero arrival stress. Here's why experienced travelers book before they fly.",
        excerpt: "The best tables go first, weekends sell out, and arrival night should be spent celebrating — not negotiating. Book before you fly.",
        category: "Guides",
        readTime: "4 min read",
        image: IMAGES.finalCta,
        sections: [
            {
                h: "The best tables go first",
                b: "Prime table positions — near the DJ, on the mezzanine, with the view — are limited by definition. On weekends and holidays they're typically reserved days in advance, leaving walk-ins with whatever remains.",
            },
            {
                h: "High season is competitive",
                b: "From November to February, Thailand's nightlife cities run at full capacity. Add festivals and public holidays, and advance booking shifts from smart to essential for any group that cares where it sits.",
            },
            {
                h: "Arrival night, minus the stress",
                b: "There's a specific pleasure in landing, dropping your bags, and knowing the night is already arranged. Your name is on the list, the table is held, and the only decision left is the first toast.",
            },
            {
                h: "How far ahead to book",
                b: "A few days is usually enough for midweek; for weekends and peak dates, earlier is better. Message BookMyVIP on WhatsApp as soon as your flights are set — even a rough date lets us hold the right options for you.",
            },
        ],
    },
    {
        slug: "what-is-nightlife-concierge",
        title: "What Is a Nightlife Concierge — and Why Travelers Use One",
        meta: "A nightlife concierge arranges VIP tables, bottle service and club access for travelers. Here's what a Bangkok nightlife concierge actually does and who it's for.",
        excerpt: "Part fixer, part planner: what a nightlife concierge actually does, and why more travelers land in Thailand with one in their pocket.",
        category: "Guides",
        readTime: "5 min read",
        image: IMAGES.experience,
        sections: [
            {
                h: "What a nightlife concierge does",
                b: "A nightlife concierge arranges the parts of a night out that are hard to do from abroad: checking what's available on your dates, explaining packages clearly, reserving VIP tables, and coordinating entry — all through one conversation.",
            },
            {
                h: "The problems it solves",
                b: "Language barriers, unfamiliar venues, unclear pricing, and the risk of a wasted night in an unfamiliar city. A concierge replaces all of that with clear options and a confirmed plan.",
            },
            {
                h: "Who uses one",
                b: "Couples upgrading a holiday evening, groups coordinating ten people across time zones, birthday and bachelor parties with one shot at getting it right — anyone who'd rather spend their trip enjoying it than organising it.",
            },
            {
                h: "How to use BookMyVIP",
                b: "Message us on WhatsApp with your destination, date, group size and the vibe you're after. Our concierge replies with suitable VIP table and nightlife options for Bangkok, Phuket, Pattaya or Koh Samui — you choose, we handle the details.",
            },
        ],
    },
    {
        slug: "best-time-visit-thailand-nightlife",
        title: "The Best Time to Visit Thailand for Nightlife",
        meta: "When to visit Thailand for the best nightlife — high season energy, shoulder-season value, weekends vs weekdays, and when to book VIP tables early.",
        excerpt: "High season energy or shoulder-season breathing room? When to plan a Thailand trip around its nightlife — and when to book tables early.",
        category: "Thailand",
        readTime: "4 min read",
        image: IMAGES.kohSamui,
        sections: [
            {
                h: "November to February: peak season",
                b: "The cool, dry months are Thailand at its busiest — full venues, international crowds and the biggest nightlife calendar of the year. It's the most electric time to go out, and the most important time to book VIP tables in advance.",
            },
            {
                h: "March to May: hot and lively",
                b: "The hot season keeps the energy high, especially around Songkran in April, when Thailand celebrates its new year. Expect busy venues and warm, late nights — ideal for groups who like their nightlife at full volume.",
            },
            {
                h: "June to October: the shoulder season",
                b: "The greener months bring fewer crowds and easier bookings, with nightlife still running strong in Bangkok, Phuket and Pattaya. A good window for travelers who want great tables with more flexibility.",
            },
            {
                h: "Weekends and holidays",
                b: "Whatever the season, Thursday to Saturday nights and public holidays are when the best tables disappear first. Tell BookMyVIP your dates early and we'll secure the strongest options available.",
            },
        ],
    },
    {
        slug: "couples-night-out-bangkok",
        title: "A Couple's Night Out in Bangkok, Upgraded",
        meta: "How to plan a premium couple's night out in Bangkok — rooftop sunsets, intimate VIP tables and a pace that suits two. Arranged by the BookMyVIP concierge.",
        excerpt: "Sunset on a rooftop, a table for two with a view, and no logistics between you — Bangkok, done properly as a couple.",
        category: "Occasions",
        readTime: "4 min read",
        image: IMAGES.bangkok,
        sections: [
            {
                h: "Begin with the skyline",
                b: "Bangkok's rooftops are built for two: golden-hour cocktails high above Sukhumvit or Silom, with the city switching on below you. It's the natural opening chapter of a premium evening.",
            },
            {
                h: "A table, not a queue",
                b: "For couples, a VIP table isn't about volume — it's about having your own corner of the room, service that comes to you, and a night that moves at your pace instead of the crowd's.",
            },
            {
                h: "Choose your mood",
                b: "Thonglor for stylish lounges, Sukhumvit for variety, RCA if you want the big-room finale. Tell us the mood and we'll match the venue and table to it.",
            },
            {
                h: "Arranged in one message",
                b: "Send BookMyVIP your date and the kind of evening you want. Our concierge will come back with VIP table options for two — rooftop start included if you'd like one.",
            },
        ],
    },
    {
        slug: "thailand-group-trip-vip-tables",
        title: "Thailand Group Trips: How to Keep the Whole Crew Together at Night",
        meta: "Planning a Thailand group trip? How VIP tables keep your whole crew together at night — booking tips for large groups in Bangkok, Phuket, Pattaya and Koh Samui.",
        excerpt: "Ten people, four opinions, one night. How a VIP table turns group-trip chaos into a night everyone actually remembers together.",
        category: "Guides",
        readTime: "4 min read",
        image: IMAGES.finalCta,
        sections: [
            {
                h: "The group-night problem",
                b: "Groups fragment. Someone queues, someone wanders, someone ends up in the wrong venue across town. The fix is structural: give the night a headquarters everyone returns to.",
            },
            {
                h: "A VIP table is your headquarters",
                b: "A reserved table with bottle service means the crew has a base — drinks handled, space guaranteed, nobody lost. People can explore and always find their way back to the group.",
            },
            {
                h: "Decide early, book early",
                b: "Large-group tables are the scarcest inventory in any venue. Agree the city, the date and a rough budget level before you fly, and book the table as soon as flights are confirmed.",
            },
            {
                h: "One chat for the whole crew",
                b: "Nominate one organiser and run everything through a single WhatsApp conversation with BookMyVIP. We'll handle availability, table size and package options for your group in Bangkok, Phuket, Pattaya or Koh Samui.",
            },
        ],
    },
    {
        slug: "phuket-vs-bangkok-nightlife",
        title: "Phuket vs Bangkok: Which Nightlife Scene Is Right for Your Trip?",
        meta: "Phuket or Bangkok for nightlife? An honest comparison of the two scenes — energy, venues, VIP tables and who each city suits best.",
        excerpt: "Rooftops and mega-clubs, or beach clubs and Bangla Road? An honest head-to-head to help you pick the right city for your nights.",
        category: "Thailand",
        readTime: "5 min read",
        image: IMAGES.phuket,
        sections: [
            {
                h: "Bangkok: scale and variety",
                b: "Bangkok is the heavyweight — rooftop bars, mega-clubs in RCA, the lounges of Thonglor and everything in between, every night of the week. If your trip is short and you want maximum choice, Bangkok rarely misses.",
            },
            {
                h: "Phuket: island energy",
                b: "Phuket trades scale for setting: sunset beach clubs on the west coast and the famous intensity of Bangla Road in Patong. Nights here come with sand, sea air and a holiday rhythm the capital can't replicate.",
            },
            {
                h: "Who should pick which",
                b: "Choose Bangkok for variety, rooftops and big-club production. Choose Phuket for beach-club days that become VIP nights. Many travelers do both — a few nights in the capital, then south to the island.",
            },
            {
                h: "Either way, book ahead",
                b: "BookMyVIP arranges VIP tables in both cities. Tell us your dates and which scene sounds like you — or ask our concierge to help you decide between them honestly.",
            },
        ],
    },
];
